import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AvatarActions } from 'mojo-avatar';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';

import * as CompaniesPageActions from '@modules/companies/store/companies-page.actions';
import {
  createManagerPerCompanyWithPhoto,
  updateManagerPerCompany,
} from '@modules/companies/store/companies-page.actions';
import { DangerAlert, SuccessAlert } from '@shared/alert';
import { Attachment, Company } from '@shared/models';
import {
  AssociationModule,
  AttachmentParams,
  AttachmentService,
  CompanyService,
} from '@shared/services';

import { JobsActionTypes } from '../jobs/jobs.actions';
import * as CompaniesActions from './companies.actions';

@Injectable()
export class CompaniesEffects {
  get$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesGet, JobsActionTypes.LoadCompany),
      mergeMap(({ id }) =>
        this.service
          .get({ url: id.toString() })
          .pipe(map((company) => CompaniesActions.companiesGetSuccess({ company }))),
      ),
    ),
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesLoad),
      mergeMap(() =>
        this.service.get().pipe(
          map((response) => CompaniesActions.companiesLoadSuccess({ companies: response })),
          catchError((error) => of(CompaniesActions.companiesLoadFail(error))),
        ),
      ),
    ),
  );

  loadFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesLoadFail),
      map(() => new DangerAlert('There was a problem loading companies')),
    ),
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesCreate),
      switchMap(({ company, photo }) => {
        company = {
          ...company,
          socialInfos: company.socialInfos.filter(({ url }) => !!url),
        };
        return this.service.post({ body: company }).pipe(
          map((response: Company) =>
            !!photo
              ? CompaniesActions.companyObjectSavePhoto({
                  company: response,
                  photo,
                  associationModule: AssociationModule.COMPANY,
                })
              : CompaniesActions.companiesCreateSuccess({
                  company: response,
                }),
          ),
          catchError((error) => of(CompaniesActions.companiesCreateFail({ error }))),
        );
      }),
    ),
  );

  addPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companyObjectSavePhoto),
      switchMap(({ company, manager, photo, associationModule }) => {
        const id = !!company ? company.id : manager.id;
        const attachment: AttachmentParams = {
          associateModuleId: id,
          associationModule,
          associationType: AttachmentService.AssociationType.IMAGE,
          name: 'logo.svg',
        };
        return this.attachmentService.save(photo, attachment).pipe(
          map((attachmentResponse: Attachment) => {
            if (!!company) {
              return CompaniesActions.companiesCreateWithPhoto({
                company: { ...company, logoId: attachmentResponse.id },
              });
            } else {
              return createManagerPerCompanyWithPhoto({
                manager: { ...manager, photoId: attachmentResponse.id },
              });
            }
          }),
          catchError((error) => of(CompaniesActions.companiesCreateFail({ error }))),
        );
      }),
    ),
  );

  createWithPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesCreateWithPhoto),
      switchMap(({ company }) =>
        this.service.put({ id: company.id, body: company }).pipe(
          map((response: Company) =>
            CompaniesActions.companiesCreateSuccess({ company: response }),
          ),
          catchError((error) => of(CompaniesActions.companiesCreateFail({ error }))),
        ),
      ),
    ),
  );

  createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesCreateSuccess),
      map(() => new SuccessAlert('Company has been created')),
    ),
  );

  createFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesCreateFail),
      map(() => new DangerAlert('There was a problem creating company')),
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesUpdate),
      switchMap(({ company }) => {
        company = {
          ...company,
          socialInfos: company.socialInfos.filter(({ url }) => !!url),
        };
        return this.service.put({ id: company.id, body: company }).pipe(
          map((response: Company) =>
            CompaniesActions.companiesUpdateSuccess({ company: response }),
          ),
          catchError((error) => of(CompaniesActions.companiesUpdateFail({ error }))),
        );
      }),
    ),
  );

  updatePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companyObjectUpdateWithPhoto),
      switchMap(({ company, manager, photo, associationModule }) => {
        const logoId = !!company ? company.logoId : manager.photoId;
        const id = !!company ? company.id : manager.id;
        const attachment: AttachmentParams = {
          associateModuleId: id,
          associationModule,
          associationType: AttachmentService.AssociationType.IMAGE,
          name: 'logo.svg',
        };
        const attachmentUpsert$ = !!logoId
          ? this.attachmentService.update(photo, logoId)
          : this.attachmentService.save(photo, attachment);
        return attachmentUpsert$.pipe(
          concatMap((attachmentResponse: Attachment) => {
            if (!!company) {
              return [
                AvatarActions.avatarReload({
                  avatar: { id: attachmentResponse.id },
                }),
                CompaniesActions.companiesUpdate({
                  company: { ...company, logoId: attachmentResponse.id },
                }),
              ];
            } else {
              return [
                AvatarActions.avatarReload({
                  avatar: { id: attachmentResponse.id },
                }),
                updateManagerPerCompany({
                  manager: { ...manager, photoId: attachmentResponse.id },
                }),
              ];
            }
          }),
          catchError((error) => of(CompaniesActions.companiesUpdateFail({ error }))),
        );
      }),
    ),
  );

  updateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesUpdateSuccess),
      map(() => new SuccessAlert('Company has been updated')),
    ),
  );

  updateFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.companiesUpdateFail),
      map(() => new DangerAlert('There was a problem with update')),
    ),
  );

  saveSuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CompaniesActions.companiesCreateSuccess, CompaniesActions.companiesUpdateSuccess),
        concatMap(({ company }) => [
          CompaniesPageActions.companiesLoadPage({}),
          this.router.navigate(['/companies/' + company.id]),
        ]),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private service: CompanyService,
    private attachmentService: AttachmentService,
    private router: Router,
  ) {}
}
